import React, { useMemo, useState } from "react";
import axios from "utils/axios";
import { useSnackbar } from "notistack";
import { UPDATE_RFQ_AWARD_JUSTIFICATION_URL } from "config/services";
import AwardJustificationModal from "./AwardJustificationModal";
import {
  buildAwardJustificationContext,
  getNonL1JustificationOptions,
  hasAwardJustificationHistory,
} from "./awardJustificationUtils";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const hasSelectionEntries = (selectionMap = {}) =>
  Object.values(selectionMap || {}).some((entries) => Array.isArray(entries) && entries.length > 0);

const AwardJustificationPanel = ({
  rfqData,
  userProfile,
  suppliersWithQuotation = [],
  selectedMaterialsMap = {},
  onUpdated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const hasHistory = hasAwardJustificationHistory(rfqData);
  const history = rfqData?.awardJustifications || [];
  const latest = history[history.length - 1] || {};
  const previousHistory = history.slice(0, -1).reverse();
  const options = getNonL1JustificationOptions(userProfile?.userEntity);
  const selectedOptions = Array.isArray(latest?.options) ? latest.options : [];
  const isPendingApproval = String(rfqData?.status || "").toUpperCase() === "PENDING APPROVAL";
  const isRfqCreator = String(rfqData?.requesterId || "") === String(userProfile?.userId || "");
  const canEdit = isPendingApproval && !isRfqCreator;
  const editContext = useMemo(() => {
    const effectiveSelectedMaterialsMap = hasSelectionEntries(selectedMaterialsMap)
      ? selectedMaterialsMap
      : rfqData?.selectedMaterialsMap || {};
    const currentContext = buildAwardJustificationContext({
      rfqData,
      rfqType: rfqData?.rfqType,
      suppliersWithQuotation,
      selectedMaterialsMap: effectiveSelectedMaterialsMap,
    });

    return {
      ...currentContext,
      title: "Update award justification",
      summary: "Edit the recorded business reason for this award. A new history entry will be stored with your user details.",
      triggerType: currentContext?.triggerType || latest?.triggerType,
    };
  }, [latest?.triggerType, rfqData, selectedMaterialsMap, suppliersWithQuotation]);

  if (!hasHistory) return null;

  const handleSubmit = async (nextOptions, remark) => {
    try {
      setIsSaving(true);
      await axios.patch(UPDATE_RFQ_AWARD_JUSTIFICATION_URL, {
        rfqId: rfqData?.rfqId,
        entityId: rfqData?.entityId,
        options: nextOptions,
        remark,
        stage: "APPROVAL_JUSTIFICATION_EDIT",
        addedBy: {
          userId: userProfile?.userId,
          name: userProfile?.name,
          email: userProfile?.email,
        },
      });
      setIsEditing(false);
      enqueueSnackbar("Award justification updated", { variant: "success", autoHideDuration: 2500 });
      onUpdated?.();
    } catch (error) {
      console.error("Failed to update award justification:", error);
      enqueueSnackbar("Failed to update award justification", { variant: "error", autoHideDuration: 3000 });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-amber-950">Award justification</h3>
          {latest?.triggerType ? (
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
              {latest.triggerType === "SINGLE_QUOTATION" ? "Single quotation" : "Non-L1 selection"}
            </span>
          ) : null}
          {selectedOptions.map((option) => (
            <span
              key={option}
              className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-amber-200"
            >
              {option}
            </span>
          ))}
          {latest?.remark ? (
            <span className="rounded-md bg-white px-2.5 py-1 text-xs font-medium text-amber-950 ring-1 ring-amber-200">
              Remark: {latest.remark}
            </span>
          ) : null}
          <span className="text-xs text-amber-800">
            Added by {latest?.addedBy?.name || "User"}
            {formatDateTime(latest?.addedAt) ? ` on ${formatDateTime(latest.addedAt)}` : ""}
          </span>
          {history.length > 1 ? (
            <button
              type="button"
              onClick={() => setShowHistory((prev) => !prev)}
              className="text-xs font-semibold italic text-blue-700 underline underline-offset-2 hover:text-blue-800"
            >
              {showHistory ? "Hide trail" : `${history.length} updates recorded`}
            </button>
          ) : null}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100"
          >
            Edit
          </button>
        )}
      </div>

      {showHistory && previousHistory.length > 0 && (
        <div className="mt-2 rounded-md border border-amber-200 bg-white/80 p-2">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">Previous trail</p>
          <div className="space-y-1.5">
            {previousHistory.map((entry, index) => {
              const entryOptions = Array.isArray(entry?.options) ? entry.options : [];
              return (
                <div
                  key={`${entry?.addedAt || "history"}-${index}`}
                  className="flex flex-wrap items-center gap-2 rounded border border-amber-100 bg-amber-50/60 px-2 py-1.5 text-xs"
                >
                  <span className="font-medium text-amber-950">
                    {entry?.addedBy?.name || "User"}
                    {formatDateTime(entry?.addedAt) ? ` | ${formatDateTime(entry.addedAt)}` : ""}
                  </span>
                  {entry?.remark ? (
                    <span className="rounded bg-white px-2 py-0.5 text-[11px] font-medium text-amber-950 ring-1 ring-amber-200">
                      Remark: {entry.remark}
                    </span>
                  ) : null}
                  {entryOptions.map((option) => (
                    <span
                      key={`${entry?.addedAt}-${option}`}
                      className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 ring-1 ring-amber-200"
                    >
                      {option}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AwardJustificationModal
        open={isEditing}
        options={options}
        initialSelectedOptions={selectedOptions}
        initialRemark={latest?.remark || latest?.remarks || ""}
        context={editContext}
        loading={isSaving}
        submitLabel="Save justification"
        onCancel={() => setIsEditing(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default AwardJustificationPanel;
